import React, { useEffect, useState, useRef, useMemo } from "react";
import Tabs from "../../../components/tabsComponent";
import BasicUI from "./basic";
import AdvancedUI from "./advanced";
import { useDispatch, useSelector } from "react-redux";
import { setBreadCrumb } from "../../../../middleware/actions/breadCrumbAction";
import CollaborationUI from "./collaboration";
import Settings from "./settings";
import BusinessImpact from "./business-impact";
import GeographyTagging from "./geography";
import ProductCategory from "./product-category";
import { fetchSingleCompany, saveCompanyAdvanced, updateOverview, fetchOverview } from "../../../../middleware/services/companyApi";
import "../style.scss";
import { getProductVariant, accessRightActionCheck, accessRightInterfaceCheck } from "../../../../utilities";
import AccessDenied from "../../../components/accessDenied";
import { setSideNavForcedActiveLink } from "../../../../middleware/actions/sideNavAction";
import { resetInternalGrid, setActiveTabs } from "../../../../middleware/actions/gridAction";
import { actionError, actionSuccess, hideLoader, showLoader } from "../../../../middleware/actions/utilityAction";
import { setClickSaveSubmitState, setOverviewUpdateStatus, setBasicDetailsCompany } from "../../../../middleware/actions/companyAction";
import { PRODUCT_VARIANT } from "../../../../constants/index";
import PropTypes from "prop-types";
import ProductListing from "./products-and-technologies";
import Tagging from "./taxonomy-tags";

const CompanyUI = (props) => {
  const productVariant = getProductVariant();
  const { accessRights: aR } = props;
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [advancedFetched, setAdvancedFetched] = useState(false);
  const [interfaceActionAccess, setInterfaceActionAccess] = useState([]);
  const [companyInformation, setCompanyInformation] = useState({
    pVariant: productVariant,
    companyName: "",
    companyFoundedYear: "",
    companyEmpSize: "",
    companyWebsite: "",
    companyPublishedDate: "",
    companyLogo: "",
  });

  const tabRef = useRef();
  const companyId = props.computedMatch?.params?.companyId || "";
  const [companyName, setCompanyName] = useState("");
  const [companyDetails, setCompanyDetails] = useState("");
  const [saveTrendTreeData, setSaveTrendTreeData] = useState(false);
  const commonState = useSelector((state) => state.companyState);
  const clickStatus = useSelector((state) => state.companyState.clickStatus);
  const CommonStateInfo = commonState?.basicDetails;
  const editData = companyId && companyId.length === 24 ? true : false;
  let TABS_NAME = ["Basic", "Advanced", "Products & Technologies", "Collaboration"];
  productVariant === PRODUCT_VARIANT.INSIDER
    ? TABS_NAME.push("Taxonomy & Tags", "Geography Tags", "Product Category Tags", "Settings", "Business Impact")
    : TABS_NAME.push("Taxonomy");

  const getCompanyDetails = async () => {
    const response = await fetchSingleCompany(companyId);
    setCompanyDetails(response.data.data);
    setCompanyName(response.data.data.companyName);
  };

  const fetchOverviewData = async () => {
    dispatch(showLoader());
    const response = await fetchOverview(companyId);
    if (response) {
      const val = response.data.data;

      setCompanyInformation({
        pVariant: productVariant,
        companyName: val.companyName,
        companyFoundedYear: val.companyFoundedYear || "-",
        companyEmpSize: val.companyEmpSize || "",
        companyWebsite: val.companyWebsite || "",
        companyPublishedDate: val.productVariant.find((item) => item.productName === productVariant)?.lastPublishedDate || new Date(),
        companyLogo: val?.companyLogo || "",
      });
    }
    dispatch(hideLoader());
  };

  useEffect(() => {
    if (companyId) {
      // getCompanyName();
      getCompanyDetails();
      fetchOverviewData();
    }
  }, [companyId]);

  // UseEffect for redux
  useEffect(() => {
    dispatch(setBasicDetailsCompany(companyInformation));
  }, [companyInformation]);

  const hashArr = {
    "#basic": "Basic",
    "#advanced": "Advanced",
    "#product": "Products & Technologies",
    "#collaboration": "Collaboration",
    "#taxonomy": "Taxonomy",
    "#geographyTags": "Geography",
    "#productCategoryTags": "Product Category",
    "#settings": "Settings",
    "#businessImapact": "Business Impact",
  };

  let tabText = "";

  if (Object.values(hashArr)[selectedIndex]?.includes("Geography")) {
    tabText = "Geography Tags";
  } else if (Object.values(hashArr)[selectedIndex]?.includes("Product Category")) {
    tabText = "Product Category Tags";
  } else if (Object.values(hashArr)[selectedIndex]?.includes("Taxonomy")) {
    tabText = "Taxonomy & Tags";
  }
   else {
    tabText = Object.values(hashArr)[selectedIndex];
  }

  const breadCrumbLinks = useMemo(() => {
    const links = [{ linkUrl: "/company", linkName: aR.moduleName, linkActive: false }];
    if (!props.editData) {
      links.push({
        linkUrl: `/company/add#basic`,
        linkName: "Add New",
        linkActive: true,
      });
    }

    if (companyId && companyName) {
      links.push({
        linkUrl: companyId ? `/company/${companyId}/edit#basic` : `/company/add#basic`,
        linkName: companyName ? companyName : "Add New",
        linkActive: false,
      });
      links.push({
        linkUrl: `/company/${companyId}/edit${Object.keys(hashArr)[selectedIndex]}`,
        linkName: tabText,
        linkActive: true,
      });
    }

    return links;
  }, [companyId, companyName, aR.moduleName, selectedIndex, tabText]);

  const dispatch = useDispatch();
  const created = (e) => {
    tabRef.current.animation.previous = { effect: "None" };
    tabRef.current.animation.next = { effect: "None" };

    if (companyId !== "") {
      if (selectedIndex !== Object.keys(hashArr).indexOf(props.location.hash)) {
        setSelectedIndex(Object.keys(hashArr).indexOf(props.location.hash));
        tabRef.current.select(Object.keys(hashArr).indexOf(props.location.hash));
      }
    } else {
      tabRef.current.enableTab(1, false);
      tabRef.current.enableTab(2, false);
      tabRef.current.enableTab(3, false);
      tabRef.current.enableTab(4, false);
      tabRef.current.enableTab(5, false);
      tabRef.current.enableTab(6, false);
      tabRef.current.select(0);
    }
  };

  useEffect(() => {
    dispatch(setBreadCrumb(breadCrumbLinks));
    dispatch(setSideNavForcedActiveLink("/company"));
    return () => {
      dispatch(setSideNavForcedActiveLink());
    };
  }, [dispatch, selectedIndex, companyName, props.location.hash]);

  useEffect(() => {
    const options = {
      // damping:0.1,
      thumbMinSize: 20,
      alwaysShowTracks: true,
    };
    setTimeout(() => {
      document.querySelector(".gennx-content-wrapper");
    }, 2000);
  }, []);

  const newRecord = () => {
    tabRef.current.enableTab(1, true);
    tabRef.current.enableTab(2, true);
    tabRef.current.enableTab(3, true);
    tabRef.current.enableTab(4, true);
    tabRef.current.enableTab(5, true);
    tabRef.current.enableTab(6, true);
    tabRef.current.select(0);
  };

  const tabSelected = useSelector((state) => state.gridState.internalGridState.tabSelected);

  const sanitizeInput = (val) => {
    if (typeof val !== "string") return "";
    return val.replace(/<p>(&nbsp;|\s)*<\/p>/gi, "").trim();
  };

  const handleCommonSave = (isSubmit = false) => {
    dispatch(showLoader());
    try {
      let basicDetails = CommonStateInfo || {};
      console.log("🚀 ~ handleCommonSave ~ basicDetails:", basicDetails);

      if (Object.keys(basicDetails.length > 0)) {
        if (Object.keys(commonState?.advanceDetails)?.length > 0) {
          if (!commonState?.advanceDetails?.companyAbout) return dispatch(actionError("Please Write Few things about the company!"));
        }
        if (basicDetails.companyName?.trim() === "") return dispatch(actionError("Company Name Cannot be empty!"));
        let payload = {};
        payload.companyWorkInProgress = basicDetails?.companyWorkInProgress;
        payload.companyName = basicDetails?.companyName;
        payload.companyLegalStatus = basicDetails?.companyLegalStatus;
        payload.companyIsStartup = basicDetails?.companyIsStartup;
        payload.companyFoundedYear = basicDetails?.companyFoundedYear;
        payload.companyEmpSize = basicDetails?.companyEmpSize;
        payload.companyEmail = basicDetails?.companyEmail;
        payload.companyWebsite = basicDetails?.companyWebsite;
        payload.companyParent = {
          companyName: basicDetails?.companyParent,
        };
        payload.companySocial = {
          twitter: basicDetails?.companySocialTwitter,
          facebook: basicDetails?.companySocialFacebook,
          linkedIn: basicDetails?.companySocialLinkedIn,
        };
        payload.companyPublishedDate = basicDetails?.companyPublishedDate;
        if (basicDetails?.companyStatus === "Acquired By") {
          payload.companyStatus = {
            value: basicDetails?.companyStatus,
            acquiredBy: {
              companyName: basicDetails?.companyAcquiredBy,
            },
          };
        } else {
          payload.companyStatus = {
            value: basicDetails?.companyStatus,
          };
        }

        let saveOverviewData = new FormData();
        for (let uKey in payload) {
          if (typeof payload[uKey] != "object") {
            if (payload[uKey] && payload[uKey] !== "") saveOverviewData.append(uKey, payload[uKey]);
          } else {
            if (
              payload[uKey].acquiredBy &&
              (payload[uKey].acquiredBy?.companyId == payload[uKey].acquiredBy?.companyName ||
                payload[uKey].acquiredBy?.companyName.includes("Create"))
            ) {
              const value = {
                value: "Acquired By",
                acquiredBy: {
                  companyName: payload[uKey].acquiredBy.companyId,
                },
              };
              saveOverviewData.append(uKey, JSON.stringify(value));
            } else {
              if (payload[uKey] && payload[uKey] !== "") saveOverviewData.append(uKey, JSON.stringify(payload[uKey]));
              if (uKey == "companyLegalStatus" && !payload[uKey]) saveOverviewData.append(uKey, "");
            }
          }
        }

        if (commonState?.basicDetails?.companyName !== "" && commonState?.basicDetails?.companyWebsite !== "") {
          updateOverview(companyId, saveOverviewData)
            .then((response) => {
              if (response.status === 200) {
                if (commonState?.advanceDetails?.companyAbout !== "") {
                  handleCommonSaveAd(isSubmit);
                  return;
                }
              }
              dispatch(actionSuccess("Company Updated Successfully"));
            })
            .catch((err) => {
              dispatch(actionError("Something went wrong"));
            });
        } else {
          dispatch(actionError("Please enter information in basic tab"));
        }
      }
    } catch {
      dispatch(actionError("Something went wrong"));
    }
  };

  const handleCommonSaveAd = (isSubmit) => {
    if (commonState?.advanceDetails?.companyAbout !== "") {
      try {
        let advanceTabDetails = commonState?.advanceDetails || {};
        if (Object.keys(advanceTabDetails.length > 0)) {
          let payload = {};
          payload.companyAbout = sanitizeInput(advanceTabDetails?.companyAbout) || "";
          payload.companyBusinessModel = sanitizeInput(advanceTabDetails?.companyBusinessModel) || "";
          payload.companyFuturePlans = sanitizeInput(advanceTabDetails?.companyFuturePlans) || "";
          payload.companyWhatsUnique = advanceTabDetails?.companyWhatsUnique || "";
          payload.id = companyId;

          const formData = new FormData();
          const data = Object.keys(payload).reduce((formData, key) => {
            formData.append(key, payload[key]);
            return formData;
          }, new FormData());

          saveCompanyAdvanced(companyId, data)
            .then((res) => {
              if (!isSubmit) dispatch(actionSuccess("Company advanced info saved successfully"));
              if (clickStatus) dispatch(setOverviewUpdateStatus(true));
              tabRef.current.enableTab(6, true);
              dispatch(setClickSaveSubmitState());
              dispatch(hideLoader());
            })
            .catch((err) => {
              dispatch(actionError("Something went wrong"));
              dispatch(hideLoader());
            });
        }
      } catch {
        dispatch(actionError("Something went wrong"));
        dispatch(hideLoader());
      }
    } else {
      dispatch(actionError("please enter information in content tab"));
      dispatch(hideLoader());
    }
  };

  /* Interface access  */
  const interfaces = [
    "Basic",
    "Advanced",
    "Products & Technologies",
    "Collaboration",
    "Taxonomy",
    "Settings",
    "Geography",
    "Product Category",
    "Business Impact",
  ];
  const accessRightInterfaceRights = accessRightInterfaceCheck(aR.moduleName, interfaces);

  /* Interface functionality wise access  */
  useEffect(() => {
    let interfaceName = Object.values(hashArr)[selectedIndex];
    let actionAccess = accessRightActionCheck(aR.moduleName, interfaceName);
    setInterfaceActionAccess(actionAccess);
  }, [selectedIndex]);

  let interfaceAccess = accessRightInterfaceRights.includes(Object.values(hashArr)[selectedIndex]);
  return (
    <div style={{ background: "#F2F2F2", overflow: "hidden" }} className="gennx-envelope company-master">
      <Tabs
        tabsName={TABS_NAME}
        tabRef={tabRef}
        created={created}
        handleClick={() => {
          setSelectedIndex(tabRef?.current?.selectedItem);
          if (tabSelected != tabRef?.current?.selectedItem) {
            dispatch(resetInternalGrid());
          }
          dispatch(setActiveTabs(tabRef?.current?.selectedItem));
        }}
        cssClass="newHeaderTabCss company-tabs-css"
      >
        {/* <div className="company-tabs-content"> */}
        <div key="companyOverview" className="company-tab-content">
          {selectedIndex === 0 && interfaceAccess ? (
            <BasicUI
              companyId={companyId}
              selectedIndex={selectedIndex}
              handleCommonSaveAd={handleCommonSaveAd}
              editData={editData}
              setCurrentTab={(selectedIndex) => {
                tabRef.current.select(selectedIndex);
                setSelectedIndex(selectedIndex);
              }}
              {...props}
              newRecord={newRecord}
              hashArr={hashArr}
              interfaceActionAccess={interfaceActionAccess}
            />
          ) : (
            <AccessDenied />
          )}
        </div>
        <div key="companyAdvanced" className="company-tab-content">
          {selectedIndex === 1 && interfaceAccess ? (
            <AdvancedUI
              advancedFetched={advancedFetched}
              setAdvancedFetched={setAdvancedFetched}
              companyId={companyId}
              selectedIndex={selectedIndex}
              handleCommonSave={handleCommonSave}
              setCurrentTab={(selectedIndex) => {
                tabRef.current.select(selectedIndex);
                setSelectedIndex(selectedIndex);
              }}
              hashArr={hashArr}
              {...props}
              interfaceActionAccess={interfaceActionAccess}
            />
          ) : (
            <AccessDenied />
          )}
        </div>
        <div key="companyProducts" className="">
          {selectedIndex === 2 && interfaceAccess ? (
            <ProductListing
              companyId={companyId}
              selectedIndex={selectedIndex}
              setCurrentTab={(selectedIndex) => {
                tabRef.current.select(selectedIndex);
                setSelectedIndex(selectedIndex);
              }}
              {...props}
              interfaceActionAccessProduct={interfaceActionAccess}
            />
          ) : (
            <AccessDenied />
          )}
        </div>
        <div key="companyCollaboration" className="company-tab-content">
          {selectedIndex === 3 && interfaceAccess ? (
            <CollaborationUI
              companyId={companyId}
              selectedIndex={selectedIndex}
              setCurrentTab={(selectedIndex) => {
                tabRef.current.select(selectedIndex);
                setSelectedIndex(selectedIndex);
              }}
              hashArr={hashArr}
              {...props}
              interfaceActionAccessCollaboration={interfaceActionAccess}
            />
          ) : (
            <AccessDenied />
          )}
        </div>
        <div key="companyTaxonomy" className="company-tab-content">
          {selectedIndex === 4 && interfaceAccess ? (
            <Tagging
              companyId={companyId}
              selectedIndex={selectedIndex}
              setCurrentTab={(selectedIndex) => {
                tabRef.current.select(selectedIndex);
                setSelectedIndex(selectedIndex);
              }}
              hashArr={hashArr}
              {...props}
              interfaceActionAccessTaxonomy={interfaceActionAccess}
              saveTrendTreeData={saveTrendTreeData}
              setSaveTrendTreeData={setSaveTrendTreeData}
            />
          ) : (
            <AccessDenied />
          )}
        </div>
        <div key="companyGeographyTags" className="company-tab-content">
          {selectedIndex === 5 && interfaceAccess ? (
            <GeographyTagging
              companyId={companyId}
              selectedIndex={selectedIndex}
              setCurrentTab={(selectedIndex) => {
                tabRef.current.select(selectedIndex);
                setSelectedIndex(selectedIndex);
              }}
              hashArr={hashArr}
              {...props}
              interfaceActionAccess={interfaceActionAccess}
            />
          ) : (
            <AccessDenied />
          )}
        </div>
        <div key="companyProductTags" className="company-tab-content">
          {selectedIndex === 6 && interfaceAccess ? (
            <ProductCategory
              companyId={companyId}
              selectedIndex={selectedIndex}
              setCurrentTab={(selectedIndex) => {
                tabRef.current.select(selectedIndex);
                setSelectedIndex(selectedIndex);
              }}
              companyDetails={companyDetails}
              hashArr={hashArr}
              {...props}
              interfaceActionAccess={interfaceActionAccess}
            />
          ) : (
            <AccessDenied />
          )}
        </div>
        <div key="companySetting" className="company-tab-content">
          {selectedIndex === 7 && interfaceAccess ? (
            <Settings
              companyId={companyId}
              selectedIndex={selectedIndex}
              setCurrentTab={(selectedIndex) => {
                tabRef.current.select(selectedIndex);
                setSelectedIndex(selectedIndex);
              }}
              hashArr={hashArr}
              {...props}
              interfaceActionAccess={interfaceActionAccess}
            />
          ) : (
            <AccessDenied />
          )}
        </div>
        <div key="businnessImpact" className="company-tab-content">
          {selectedIndex === 8 && interfaceAccess ? (
            <BusinessImpact
              companyId={companyId}
              selectedIndex={selectedIndex}
              setCurrentTab={(selectedIndex) => {
                tabRef.current.select(selectedIndex);
                setSelectedIndex(selectedIndex);
              }}
              hashArr={hashArr}
              companyName={companyName}
              {...props}
              interfaceActionAccessBusinessImpact={interfaceActionAccess}
            />
          ) : (
            <AccessDenied />
          )}
        </div>
      </Tabs>
    </div>
  );
};
CompanyUI.propTypes = {
  accessRights: PropTypes.arrayOf(PropTypes.string),
  newsId: PropTypes.string,
  location: PropTypes.string,
  computedMatch: PropTypes.string,
  editData: PropTypes.bool,
};
export default CompanyUI;
